import {
  createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { startOfMonth } from 'date-fns';
import useMounted from 'src/common/hooks/useMounted';
import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import { formatDate } from 'src/common/utils/dateHelpers';
import { RecentInquiryData } from 'src/common/utils/recentInquiryCookieData';
import { formatDuration, getDurationHours } from 'src/listing/helper';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import { TripCartPanels, TripCartFormState } from 'src/listing/types';
import {
  checkInstabookTripsForMultiDays,
  getClosestInstabookDate,
  getInstabookDatesAroundMonth,
  getInstabookAvailabilitiesOnDate,
} from 'src/trip-cart-deprecated/utils/tripCartUtils';
import { getInitialValues } from 'src/trip-cart-deprecated/utils/tripCartInitialValues';
import { useMediaQueries } from 'src/common/providers/MediaQueryProvider';
import { getInitialCachedDates } from 'src/trip-cart-deprecated/utils/serverGetInstabookDates';
import { InstabookAvailability } from 'src/common/types/api/v4/instabook';
import {
  MIN_SNAP_POINT, TRIP_CART_PANEL_CALENDAR, TRIP_CART_PANEL_ROOT,
} from 'src/listing/constants';
import { clearCaches } from 'src/trip-cart-deprecated/utils/tripCartCache';
import { useRouter } from 'next/router';
import type { ListingPageProps } from 'src/common/types/common/ListingPageProps';
import { ListingAddon } from 'src/common/types/common/Addons';
import captureException from 'src/common/utils/captureException';
import broadcastNotification from 'src/common/components/Notification/broadcastNotification';
import { removeSearchParams } from 'src/common/hooks/useRemoveSearchParam';
import ensureString from 'src/common/utils/ensureString';
import { sessionStore } from 'src/common/utils/storageWrapper';
import { SessionData } from 'src/checkout/hooks/useProceedToCheckoutStep';
import { InquiryData } from 'src/inquiry/types';
import { decompress } from 'lz-string';
import { timeStringToDateUpdate } from 'src/common/helpers';
import { TIME_24 } from 'src/common/constants';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import { useListingMotivators } from 'src/listing/hooks/useListingPageMotivators';

type TripCartStatus = 'initializing' | 'load-instabook-durations' | 'set-instabook-duration' | 'ready';
type PanelType = 'aside' | 'drawer';

type TripCartContextProps = {
  isOpen: boolean;
  setOpen: (value: boolean, startingPanel?: TripCartPanels) => void;
  listing: FullBoatSearchDetail;
  status: TripCartStatus;
  initialValues: TripCartFormState;
  hasInstabookTripsForMultiDays?: boolean;
  instabookDurations: number[];
  loadInstabookTrips: (values: TripCartFormState) => Promise<boolean>;
  setInstabookDurations: (value: number[]) => void;
  instabookAvailabilities: InstabookAvailability[];
  setInstabookAvailabilities: (value: InstabookAvailability[]) => void;
  instabookDates: string[];
  updateInstabookDatesAroundMonth: (date: Date) => void;
  showPanel: (value: TripCartPanels, autoOpen?: boolean) => void;
  closePanel: () => void;
  activePanel: TripCartPanels;
  /** @deprecated conversion-mission */
  isLargeScreen: boolean;
  /**
   * Which is visible: mobile drawer or desktop aside
   * This is mediaQuery based, use CSS for visibility where possible in rendering
   */
  panelType: PanelType;
  threadId?: string;
  setSelectedInstabookAvailability: (value: InstabookAvailability | undefined) => void;
  selectedInstabookAvailability?: InstabookAvailability;
  /** @deprecated conversion-mission */
  snap: string;
  /** @deprecated conversion-mission */
  setSnap: (value: string) => void;
  snoozeActive?: boolean;
  addonsOptional?: ListingAddon[];
  addonsLater?: ListingAddon[];
  panelDirection: 'in' | 'out';
  /**
   * Value that determines if the AI summary is shown or not on the listing page.
   */
  showAiSummary: boolean;
};

const TripCartContext = createContext<TripCartContextProps>({} as TripCartContextProps);

type TripCartProviderProps = {
  listing: FullBoatSearchDetail;
  tripCartCookieData?: Partial<RecentInquiryData>;
  instabookDates: string[];
  snoozeActive?: boolean;
  /**
   * If set, the Inquiry Sent banner will be shown
   */
  threadId?: string;
  children: ReactNode;
  showAiSummary: boolean;
} & Pick<ListingPageProps, 'addonsOptional' | 'addonsLater'>;

const TripCartProvider: FC<TripCartProviderProps> = ({
  listing,
  tripCartCookieData,
  instabookDates: initialInstabookDates,
  snoozeActive,
  threadId,
  children,
  addonsOptional = [],
  addonsLater = [],
  showAiSummary,
}) => {
  const router = useRouter();
  const locale = useDateFnsLocale();
  const [status, setStatus] = useState<TripCartStatus>('initializing');
  const { currency } = useCurrencyContext();
  const { setMotivatorsDates } = useListingMotivators();
  const [isOpen, setOpenInternal] = useState(false);
  const [activePanel, setActivePanel] = useState<TripCartPanels>(TRIP_CART_PANEL_ROOT);
  const [previousPanels, setPreviousPanels] = useState<TripCartPanels[]>([]);
  const [panelDirection, setPanelDirection] = useState<'in' | 'out'>('in');
  const isMounted = useMounted();
  const [instabookDates, setInstabookDates] = useState<string[]>(initialInstabookDates);
  const {
    setInstabookDates: setInstabookDatesForMotivators,
    changeVisibleCalendarMonth,
  } = useBookingMotivatorsContext();
  /** month start dates for the months already retrieved */
  const [cachedInstabookMonths] = useState<Set<string>>((
    () => new Set(getInitialCachedDates(tripCartCookieData))
  ));

  // For current date
  const [
    instabookAvailabilities,
    setInstabookAvailabilities,
  ] = useState<InstabookAvailability[]>([]);
  const [instabookDurations, setInstabookDurations] = useState<number[]>([]);
  const [
    selectedInstabookAvailability,
    setSelectedInstabookAvailability,
  ] = useState<InstabookAvailability | undefined>();

  // Initialization ============================================================
  const { bookingMotivators } = useBookingMotivatorsContext();

  const [initialValues, setInitialValues] = useState((): TripCartFormState => getInitialValues({
    listing,
    bookingMotivators,
    tripCartCookieData,
  }));

  // End initialization =========================================================

  // Instabooks ===+=============================================================
  const [hasInstabookTripsForMultiDays, setInstabookTripsForMultiDays] = useState<boolean>();

  const getInitialInstabookDate = useCallback((): Date | null => {
    const initialDate = initialValues.formSelectedDates[0];
    // Keep initial date
    if (initialInstabookDates.includes(formatDate(initialDate))) {
      return initialDate;
    }

    const hasInstabooksAndNoSavedDate = (
      initialInstabookDates.length
      && !tripCartCookieData?.dates?.length
    );

    if (hasInstabooksAndNoSavedDate) {
      return getClosestInstabookDate(initialDate, initialInstabookDates);
    }
    return null;
  }, [initialInstabookDates, initialValues, tripCartCookieData?.dates]);

  const setupInstabooks = useCallback(() => {
    const multidayHasInstabooks = checkInstabookTripsForMultiDays(listing, initialInstabookDates);
    setInstabookTripsForMultiDays(multidayHasInstabooks);

    const instabookDate = getInitialInstabookDate();
    // no instabooks available within initialInstabookDates
    if (!instabookDate) {
      changeVisibleCalendarMonth(initialValues.formSelectedDates[0]);
      setStatus('ready');
      return;
    }
    if (instabookDate !== initialValues.formSelectedDates[0]) {
      setInitialValues({
        ...initialValues,
        formSelectedDates: [instabookDate],
      });
    }
    setStatus('load-instabook-durations');
  }, [
    changeVisibleCalendarMonth,
    getInitialInstabookDate,
    initialInstabookDates,
    initialValues,
    listing,
  ]);

  const setupFormData = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const sessionData = sessionStore.getItem(ensureString(router.query.sessionKey));
      if (!sessionData) {
        throw new Error('No session data');
      }

      const parsedData: SessionData | InquiryData = JSON.parse(sessionData);
      if (!parsedData) {
        throw new Error('Unable to parse session or inquiry');
      }

      // set departure time and message from session data for book now.
      if (Object.hasOwn(parsedData, 'availabilityId')) {
        const bookNowData = parsedData as SessionData;
        const { formData } = bookNowData;
        if (formData) {
          const [startDate] = initialValues.formSelectedDates;
          const dateTime = timeStringToDateUpdate(ensureString(formData.departureTime), startDate);
          const formattedTime = formatDate(dateTime, TIME_24, locale);
          setInitialValues((prev) => ({
            ...prev,
            departureTime: formattedTime,
            message: decompress(ensureString(formData.ownerMessage)),
          }));
        }
      } else {
      // set departure time and message from session data for inquiry.
        const inquiryData = parsedData as InquiryData;
        if (inquiryData) {
          setInitialValues((prev) => ({
            ...prev,
            departureTime: inquiryData.pickup_time,
            message: decompress(ensureString(inquiryData.comment)),
          }));
        }
      }
    } catch (error) {
      captureException(error);
    }

    const sessionData = sessionStore.getItem(ensureString(router.query.sessionKey));
    if (sessionData) {
      const parsedData: SessionData | InquiryData = JSON.parse(sessionData);

      if (!parsedData) {
        return;
      }

      // set departure time and message from session data for book now.
      if (Object.hasOwn(parsedData, 'availabilityId')) {
        const bookNowData = parsedData as SessionData;
        const { formData } = bookNowData;
        if (formData) {
          const [startDate] = initialValues.formSelectedDates;
          const dateTime = timeStringToDateUpdate(ensureString(formData.departureTime), startDate);
          const formattedTime = formatDate(dateTime, TIME_24, locale);
          setInitialValues((prev) => ({
            ...prev,
            departureTime: formattedTime,
            message: decompress(ensureString(formData.ownerMessage)),
          }));
        }
      } else {
        // set departure time and message from session data for inquiry.
        const inquiryData = parsedData as InquiryData;
        if (inquiryData) {
          setInitialValues((prev) => ({
            ...prev,
            departureTime: inquiryData.pickup_time,
            message: decompress(ensureString(inquiryData.comment)),
          }));
        }
      }
    }
  }, [initialValues.formSelectedDates, locale, router.query.sessionKey]);

  // adjusted from PersistTripCartPrices:calculateInstabookPrices
  const loadInstabookDurations = useCallback(async () => {
    const { durations } = await getInstabookAvailabilitiesOnDate(
      listing.id,
      initialValues.formSelectedDates[0],
      initialValues.formGuests,
      currency.code,
    ) ?? {};

    // Fail when network or API issues run past retry limit. Falls back to Inquiry
    if (!durations?.length) {
      // should not occur unless data is corrupted. Stop loading
      setStatus('ready');
      return;
    }

    setInstabookDurations(durations);
    setStatus('set-instabook-duration');
  }, [currency.code, initialValues.formGuests, initialValues.formSelectedDates, listing.id]);

  const updateInstabookDuration = useCallback(() => {
    // Skip if a duration is already chosen
    const preferredDuration = tripCartCookieData?.duration || initialValues.formDuration;
    const preferredDurationHours = getDurationHours(preferredDuration);

    let duration = instabookDurations.includes(preferredDurationHours)
      ? preferredDuration
      : null;

    // When no preference, use instabooks
    if (!tripCartCookieData?.duration) {
      duration = formatDuration(instabookDurations[0], { multiDay: false });
    }

    if (duration) {
      setInitialValues({
        ...initialValues,
        formDuration: duration,
        isMultiDay: false,
      });
    }
    setStatus('ready');
  }, [initialValues, instabookDurations, tripCartCookieData?.duration]);

  /** @returns true when data has been retrieved successfully */
  const loadInstabookTrips = useCallback(async (values: TripCartFormState): Promise<boolean> => {
    // Hard caching. Only cleared on page reload
    const { availabilities, durations } = await getInstabookAvailabilitiesOnDate(
      listing.id,
      values.formSelectedDates[0],
      values.formGuests,
      currency.code,
    ) ?? {};

    // Call aborted
    if (!durations || !availabilities) return false;

    if (!durations.length) {
      setInstabookAvailabilities([]);
      setInstabookDurations([]);
      // should not occur unless data was changed after page load
      return false;
    }

    setInstabookAvailabilities(availabilities);
    setInstabookDurations(durations);
    return true;
  }, [
    currency.code,
    listing.id,
    setInstabookDurations,
    setInstabookAvailabilities,
  ]);

  // End Instabooks =============================================================

  // Panel type: drawer or aside

  const { l: isLargeScreen } = useMediaQueries();
  const panelType: 'drawer' | 'aside' = isLargeScreen ? 'aside' : 'drawer';
  // The panel that should be default
  // For aside, the root panel is always visible, this is the first modal panel
  const rootPanel = panelType === 'drawer' ? TRIP_CART_PANEL_ROOT : TRIP_CART_PANEL_CALENDAR;

  const showPanel = useCallback((panel: TripCartPanels, autoOpen: boolean = true) => {
    // Ensure no duplicates
    const previous = previousPanels.filter((v) => v !== panel);
    setActivePanel(panel);
    if (!isOpen && autoOpen) {
      setOpenInternal(true);
      // Will show immediately instead of animating in from right
      setPanelDirection('out');
      // Clicking done will auto-close it
      setPreviousPanels([]);
    } else {
      setPreviousPanels([...previous, activePanel]);
      setPanelDirection(panel === rootPanel ? 'out' : 'in');
    }
  }, [activePanel, isOpen, previousPanels, rootPanel]);

  const closePanel = useCallback((closeAll: boolean = false) => {
    setPanelDirection('out');
    const previous = [...previousPanels];
    if (previousPanels.length > 0) {
      const last = closeAll || !previous.length
        ? rootPanel
        : previous.pop();
      setActivePanel(last ?? rootPanel);
      setPreviousPanels(closeAll ? [] : previous);
    }
    if (panelType === 'aside' && previous.length === 0) {
      setOpenInternal(false);
    }

    if (panelType === 'drawer' && previous.length === 0) {
      setActivePanel(rootPanel);
    }
  }, [panelType, previousPanels, rootPanel]);

  const [snap, setSnap] = useState(MIN_SNAP_POINT);

  /** Open drawer or side panel */
  const setPanelOpen = useCallback((value: boolean) => {
    setOpenInternal(value);
    // reset drawer
    setPreviousPanels([]);
    setActivePanel(rootPanel);
    setPanelDirection('out');
  }, [rootPanel]);

  const [previousPanelType, setPreviousPanelType] = useState<PanelType>();

  // Reset drawer or panel
  useEffect(() => {
    if (previousPanelType !== panelType) {
      setPreviousPanelType(panelType);
      setPanelOpen(false);
    }
  }, [panelType, previousPanelType, setPanelOpen]);

  /**
   * Get current, previous, and next month's instabooks.
   * Will ignore previous month if it's in the past
   */
  const updateInstabookDatesAroundMonth = useCallback(async (monthStartDate: Date) => {
    const currentMonth = startOfMonth(monthStartDate);

    const newDates = await getInstabookDatesAroundMonth(
      listing.id,
      currentMonth,
      cachedInstabookMonths,
    );

    setInstabookDates((prev) => {
      const dates = Array.from(new Set([...prev, ...Array.from(newDates)]));
      setInstabookDatesForMotivators(dates);
      return dates;
    });
  }, [cachedInstabookMonths, listing.id, setInstabookDatesForMotivators]);

  useEffect(
    () => {
      router.events.on('routeChangeStart', clearCaches);
      return () => router.events.off('routeChangeStart', clearCaches);
    },
    [router],
  );
  // Load sequencing
  useEffect(() => {
    // prevent SSR calls
    if (!isMounted) {
      // May be called multiple times, this is ok
      clearCaches();
      return;
    }

    if (status === 'initializing' && Object.keys(initialValues).length) {
      setupInstabooks();
      setMotivatorsDates(initialValues.formSelectedDates);
    } else if (status === 'load-instabook-durations') {
      loadInstabookDurations();
    } else if (status === 'set-instabook-duration') {
      updateInstabookDuration();
    }
  }, [
    isMounted,
    initialValues,
    status,
    setupInstabooks,
    loadInstabookDurations,
    updateInstabookDuration,
    setMotivatorsDates,
  ]);

  useEffect(() => {
    if (Object.hasOwn(router.query, 'sheet')) {
      setPanelOpen(true);
      setupFormData();
      setMotivatorsDates(initialValues.formSelectedDates);
      removeSearchParams(router.query, ['sheet', 'sessionKey', 'session_id'], router.replace);
      sessionStore.removeItem(ensureString(router.query.sessionKey));
    }

    if (Object.hasOwn(router.query, 'error')) {
      broadcastNotification({
        i18nKey: 'inquiry-success--broadcast-boats-error-message',
        translation: 'inquiry-success',
        variant: 'error',
      });
      removeSearchParams(router.query, ['error'], router.replace);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo((): TripCartContextProps => ({
    initialValues,
    status,
    isOpen,
    listing,
    hasInstabookTripsForMultiDays,
    loadInstabookTrips,
    instabookDurations,
    setInstabookDurations,
    updateInstabookDatesAroundMonth,
    instabookDates,
    instabookAvailabilities,
    setInstabookAvailabilities,
    activePanel,
    showPanel,
    closePanel,
    isLargeScreen,
    panelType,
    selectedInstabookAvailability,
    setSelectedInstabookAvailability,
    threadId,
    snap,
    setSnap,
    snoozeActive: !!snoozeActive,
    addonsOptional,
    addonsLater,
    panelDirection,
    setOpen: setPanelOpen,
    showAiSummary,
  }), [
    initialValues,
    isOpen,
    hasInstabookTripsForMultiDays,
    instabookDates,
    instabookDurations,
    instabookAvailabilities,
    loadInstabookTrips,
    listing,
    status,
    activePanel,
    showPanel,
    closePanel,
    isLargeScreen,
    panelType,
    updateInstabookDatesAroundMonth,
    selectedInstabookAvailability,
    threadId,
    snap,
    setSnap,
    snoozeActive,
    addonsOptional,
    addonsLater,
    panelDirection,
    setPanelOpen,
    showAiSummary,
  ]);

  return (
    <TripCartContext.Provider value={value}>
      {children}
    </TripCartContext.Provider>
  );
};

export default TripCartProvider;

export const useTripCartContext = (): TripCartContextProps => {
  const context = useContext(TripCartContext);
  if (context == null) {
    throw new Error('useTripCartContext must be used within a TripCartProvider');
  }
  return context;
};
